/**
 * RecsCarouselTile — wraps HecProductTileCms for the recommendations carousels.
 *
 * Per QA (SFCC-11590), every recs tile exposes a Quick View affordance —
 * master, variant, and simple products alike. The QV productId is resolved
 * via `resolveMasterIdForQuickView`:
 *
 *   - Master hits   → open QV against the master itself.
 *   - Variant hits  → open QV against the parent master so the shopper can
 *                     see/pick any variant (not just the SKU CIO recommended).
 *   - Simple hits   → open QV against the product itself (mini-PDP view).
 *
 * History:
 *   - SFCC-11590 first pass gated QV on `isMasterProduct` only.
 *   - QA pass 1 failure: variant-of-master hits had no QV, so multi-variant
 *     products were inconsistent depending on which id CIO returned.
 *   - QA pass 2 directive (this commit): show QV on every tile regardless
 *     of product type — variant routing to master master still applies.
 *
 * Master-field propagation depends on `productHitToHecProduct` carrying
 * `master.masterId` + `c_parentMasterProductID` through the CIO → SCAPI →
 * HecProduct adapter. See `app/hec/utils/product-adapters.ts`.
 */
import { memo, useState } from "react";

import type { ProductHitWithSwatches } from "~/shop/products/productSearch.server";

import {
  ProductQuickView,
  ProductQuickViewDialog,
} from "~/components/products/product-quick-view";
import { HecProductTileCms } from "~/hec/components/product/product-tile";

import { resolveMasterIdForQuickView } from "./recs-master-id";
import type { RecsCarouselTileProps } from "./types";

const RecsCarouselTileComponent = ({
  product,
  collapseStepperAtMin = false,
  compactAddLabel = false,
  gtmListId,
  gtmListName,
  gtmIndex,
  enableRatings,
  borderColor,
  enableAddToCart,
  enableQuickView,
  enableFavorites = false,
  enableLowStockBadge,
}: RecsCarouselTileProps) => {
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  // Tile stepper qty at QV open — pre-fills the buy form (SFCC-11825).
  const [quickViewQty, setQuickViewQty] = useState<number | undefined>();
  const productIdForQV = resolveMasterIdForQuickView(product);

  return (
    <>
      <HecProductTileCms
        product={product}
        ratingsVariant="default"
        enableRatings={enableRatings}
        // Fill the slide like ProductCarousel's tiles, so a row's borders line up.
        className={borderColor ? "h-full border-2 border-solid" : undefined}
        style={borderColor ? { borderColor } : undefined}
        enableAddToCart={enableAddToCart}
        enableFavorites={enableFavorites}
        enableLowStockBadge={enableLowStockBadge}
        enableQuickView={enableQuickView}
        onOpenQuickView={quantity => {
          setQuickViewQty(quantity);
          setQuickViewOpen(true);
        }}
        collapseStepperAtMin={collapseStepperAtMin}
        compactAddLabel={compactAddLabel}
        gtmListId={gtmListId}
        gtmListName={gtmListName}
        gtmIndex={gtmIndex}
      />

      <ProductQuickViewDialog
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
      >
        <ProductQuickView
          product={{ productId: productIdForQV } as ProductHitWithSwatches}
          initialQuantity={quickViewQty}
          onClose={() => setQuickViewOpen(false)}
        />
      </ProductQuickViewDialog>
    </>
  );
};
RecsCarouselTileComponent.displayName = "RecsCarouselTileComponent";

export const RecsCarouselTile = memo(
  RecsCarouselTileComponent,
  (prev, next) =>
    prev.product.id === next.product.id &&
    prev.collapseStepperAtMin === next.collapseStepperAtMin &&
    prev.compactAddLabel === next.compactAddLabel &&
    // GTM click context must participate in the memo: a click reads these props
    // live, and e.g. ProductRecommendations' `cioHeading` lands after the first
    // paint — without this a click would emit a stale list name.
    prev.gtmListId === next.gtmListId &&
    prev.gtmListName === next.gtmListName &&
    prev.gtmIndex === next.gtmIndex &&
    prev.enableRatings === next.enableRatings &&
    prev.borderColor === next.borderColor &&
    prev.enableAddToCart === next.enableAddToCart &&
    prev.enableQuickView === next.enableQuickView &&
    prev.enableFavorites === next.enableFavorites &&
    prev.enableLowStockBadge === next.enableLowStockBadge,
);
RecsCarouselTile.displayName = "RecsCarouselTile";
